import React, { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from 'antd';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { SizeCodeForBooking, SizeCodeType } from 'entities/SizeCode';
import { RentOptionDocument } from 'entities/RentOption';
import { Box } from 'entities/Box';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { getLoggedUserData } from 'entities/User';
import { getBoxInfo, getWarehouseInfo } from 'pages/BookingPage';
import { WarehouseMap } from 'widgets/SelectBoxSizeForm/ui/WarehouseMap';
import { Button } from 'shared/ui/Button';
import { ModernBoxCardMobile, ModernBoxesContainerMobile } from './ModernBoxesContainerMobile';
import { ReactComponent as CloseIcon } from 'shared/assets/icons/CloseIcon.svg';
import { useGetFloorsByWarehouseIdQuery } from 'entities/Floor';
import { formatDecimal } from 'shared/utils/helpers/formatDecimal';

interface BoxSelectionModalMobileProps {
  isOpen: boolean;
  sizeCode: SizeCodeForBooking;
  rentOptions: RentOptionDocument[];
  selectedBoxId: string | undefined;
  onClose: () => void;
  onSelectBox: (box: Box) => void;
  onBookBox: (box: Box) => void;
}

export const BoxSelectionModalMobile: FC<BoxSelectionModalMobileProps> = memo((props) => {
  const { isOpen, sizeCode, rentOptions, selectedBoxId, onClose, onSelectBox, onBookBox } = props;
  const { square, sizeCodeType, sizeCodeId } = sizeCode;

  const { t } = useAppTranslation('booking');
  const loggedUser = useAppSelector(getLoggedUserData);
  const warehouseInfo = useAppSelector(getWarehouseInfo);
  const selectedBox = useAppSelector(getBoxInfo);
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
  const { data: floors } = useGetFloorsByWarehouseIdQuery(warehouseInfo?.warehouseId, { skip: !warehouseInfo?.warehouseId });
  const hasMap = Boolean(floors?.some((floor) => floor.shapesConfig || floor.floorSchemaBackgroundUrl));

  // Filter discounts for this size code (based on original SizeCodeCollapse logic)
  const rentDiscounts = useMemo(() => {
    return rentOptions.filter((option) => option.unitSizes?.includes(square.toString())) || [];
  }, [rentOptions, square]);

  const generalDiscounts = useMemo(() => {
    return rentOptions.filter((option) => !option.unitSizes && option.showAsPromotion) || [];
  }, [rentOptions]);

  useEffect(() => {
    if (!hasMap && viewMode === 'map') {
      setViewMode('list');
    }
  }, [hasMap, viewMode]);

  const switchToList = useCallback(() => {
    setViewMode('list');
  }, []);

  const switchToMap = useCallback(() => {
    if (hasMap) {
      setViewMode('map');
    }
  }, [hasMap]);

  const handleMapSelectBox = useCallback(
    (box: Box) => {
      onSelectBox(box);
    },
    [onSelectBox],
  );

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width="100vw"
      style={{ top: 0, maxWidth: '100vw', height: '100vh', margin: 0, padding: 0 }}
      className="mobile-box-selection-modal [&_.ant-modal-content]:overflow-hidden [&_.ant-modal-content]:p-0 [&_.ant-modal]:overflow-hidden"
      styles={{
        body: { height: '100vh', padding: 0, margin: 0 },
        content: { height: '100vh', overflow: 'hidden', margin: 0, borderRadius: 0 },
      }}
      destroyOnClose
      closable={false}
      centered={false}
    >
      <div className="h-full flex flex-col bg-backgroundColor">
        {/* Header */}
        <div className="flex items-center justify-between p-4 bg-backgroundColor border-b border-gray-200">
          <div className="flex-1">
            <h2 className="ml-2 text-xl font-semibold text-primary font-tektur">{`${t('Boxes')} ${formatDecimal(square)} м${
              sizeCodeType === SizeCodeType.CUBIC_BOX ? '³' : '²'
            }`}</h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors">
            <CloseIcon className="[&>path]:stroke-primary" width={25} height={25} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0">
          {viewMode === 'list' ? (
            <ModernBoxesContainerMobile
              sizeCodeId={sizeCodeId}
              selectedBoxId={selectedBoxId}
              rentDiscounts={rentDiscounts}
              generalDiscounts={generalDiscounts}
              onSelectBox={onSelectBox}
              onBookBox={onBookBox}
              accountType={loggedUser?.accountType}
              warehouseInfo={warehouseInfo}
            />
          ) : (
            <div className="h-full flex flex-col min-h-0">
              {warehouseInfo?.warehouseId ? (
                <WarehouseMap
                  selectedWarehouseId={warehouseInfo.warehouseId}
                  selectedBoxId={selectedBoxId}
                  allowedSizeCodeId={sizeCodeId}
                  preferredFloorId={selectedBox?.floor?.floorId}
                  containerClassName="h-full"
                  centerOnSelected
                  onSelectBox={(box, _sizeCodeId) => {
                    handleMapSelectBox(box);
                  }}
                />
              ) : (
                <div className="h-full flex items-center justify-center text-secondaryLight">{t('Warehouse not selected')}</div>
              )}
              {selectedBox && (
                <div className="mt-3 px-4">
                  <ModernBoxCardMobile
                    box={selectedBox}
                    selectedBoxId={selectedBoxId}
                    hasRentDiscounts={rentDiscounts.length > 0}
                    hasGeneralDiscounts={generalDiscounts.filter(({ showAsPromotion }) => showAsPromotion).length > 0}
                    onSelectBox={onSelectBox}
                    onBookBox={onBookBox}
                    accountType={loggedUser?.accountType}
                    warehouseInfo={warehouseInfo}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Switcher */}
        <div className="flex justify-center py-3 px-4 gap-2">
          <Button theme={viewMode === 'list' ? 'primary' : 'secondary'} size="small" fontSize="small" onClick={switchToList}>
            {t('List')}
          </Button>
          <Button
            theme={viewMode === 'map' ? 'primary' : 'secondary'}
            size="small"
            fontSize="small"
            onClick={switchToMap}
            isDisabled={!hasMap}
          >
            {t('Map')}
          </Button>
        </div>
      </div>
    </Modal>
  );
});
